import React, { useContext, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useTranslate from '~/hooks/useTranslate';
import { RootState } from '~/store/types/sharedTypes';
import { pushToGtmOnboardingAction } from '~/store/actions/gtmActions';
import { getCurrency } from '@qred/shared-component-library/src/currency';
import { updateForm } from '~/store/slices/onboardingApplication.slice';
import {
  Button,
  Group,
  Stack,
  Select,
  Body,
  Label,
} from '@qred/components-library';
import { ValidationContext } from '~/components/hoc/withValidation';
import { getTurnoverOptions, TurnoverOption } from '~/helpers/turnover.helper';

const FinancialInformation = () => {
  const validationContext = useContext(ValidationContext);

  const dispatch = useDispatch();

  const {
    onboardingApplication: { form },
    intl: { market },
    matchedMedia: { mobile },
  } = useSelector((state: RootState) => state);

  const translate = useTranslate();

  const formattedTurnoverOptions = getTurnoverOptions(market);

  useEffect(() => {
    const hasAnsweredForeignCurrenciesQuestion =
      typeof form.incomeReceivedInForeignCurrencies === 'boolean';
    if (!hasAnsweredForeignCurrenciesQuestion || !form.turnover) {
      validationContext.manuallyInvalidateForm(true);
    }

    return () => {
      validationContext.manuallyInvalidateForm(false);
    };
  }, [form.incomeReceivedInForeignCurrencies, form.turnover]);

  const onTurnoverChange = (selectedOption: TurnoverOption) => {
    if (selectedOption) {
      dispatch(
        updateForm({
          turnover: selectedOption.value,
        })
      );
    }
  };

  const onClick = (answer: boolean) => {
    dispatch(
      updateForm({
        incomeReceivedInForeignCurrencies: answer,
      })
    );

    dispatch(
      pushToGtmOnboardingAction({
        actionName: `incomeReceivedInForeignCurrencies: ${answer}`,
      })
    );

    dispatch(
      pushToGtmOnboardingAction({
        actionName: 'financial_information_change',
        stepActionNumber: 0.1,
      })
    );
  };

  return (
    <Stack spacing={'xl'}>
      <Select
        className="turnover_step_amount"
        options={formattedTurnoverOptions}
        onChange={onTurnoverChange}
        value={formattedTurnoverOptions.find(
          (option) => option.value === form.turnover
        )}
        label={
          <Label size="md">
            {translate('Onboarding.TurnoverLabel')} {getCurrency(market).unit}
          </Label>
        }
        placeholder={translate('Onboarding.TurnoverSelectInterval') as string}
        isSearchable={false}
      />
      <Stack spacing={'md'}>
        <Body size="lg">
          {translate('Onboarding.TurnoverForeignCurrencyQuestion')}
        </Body>
        <Group direction="row">
          <Button
            data-cy="foreign_currency_question_button"
            size="md"
            onClick={() => onClick(true)}
            variant="secondary"
            style={{ flexGrow: mobile ? 1 : 0 }}
            data-active={form.incomeReceivedInForeignCurrencies === true}
          >
            {translate('Yes')}
          </Button>
          <Button
            data-cy="foreign_currency_question_button"
            size="md"
            onClick={() => onClick(false)}
            variant="secondary"
            style={{ flexGrow: mobile ? 1 : 0 }}
            data-active={form.incomeReceivedInForeignCurrencies === false}
          >
            {translate('No')}
          </Button>
        </Group>
      </Stack>
    </Stack>
  );
};

export default FinancialInformation;
